import React, { useCallback, useEffect, useRef } from 'react';

const BAR_WIDTH = 2;
const SPACING = 1;
const BAR_UNIT = BAR_WIDTH + SPACING;
const AMPLITUDE_FACTOR = 8;
const FRAME_SKIP = 4;
const FILL_COLOR = '#b13030';
const VERTICAL_PADDING = 20;

export function useWaveformVisualization(
	canvasRef: React.RefObject<HTMLCanvasElement | null>,
) {
	const audioContextRef = useRef<AudioContext | null>(null);
	const analyserNodeRef = useRef<AnalyserNode | null>(null);
	const waveformBufferRef = useRef<number[]>([]);
	const frameCountRef = useRef(0);
	const animFrameRef = useRef(0);

	const drawWaveform = useCallback(() => {
		const canvas = canvasRef.current;
		const analyserNode = analyserNodeRef.current;
		if (!canvas || !analyserNode) return;

		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		frameCountRef.current++;

		const waveformBuffer = waveformBufferRef.current;
		const bufferLength = analyserNode.frequencyBinCount;
		const dataArray = new Uint8Array(bufferLength);
		analyserNode.getByteFrequencyData(dataArray);

		// compute amplitude from frequency data (0-255 range)
		const averageAmplitude =
			dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
		const scaledAmplitude =
			(averageAmplitude / 255) * (canvas.height / 2) * AMPLITUDE_FACTOR;

		// slow down the movement
		if (frameCountRef.current % FRAME_SKIP === 0) {
			waveformBuffer.shift();
			waveformBuffer.push(scaledAmplitude);
		}

		// clear the canvas
		ctx.clearRect(0, 0, canvas.width, canvas.height);

		// draw bars
		const maxAmplitude = (canvas.height / 2) - VERTICAL_PADDING;
		const midLine = canvas.height / 2;
		const totalBars = waveformBuffer.length;
		const totalBarSpace = totalBars * BAR_UNIT;
		const scale = canvas.width / totalBarSpace;

		ctx.fillStyle = FILL_COLOR;

		for (let i = 0; i < totalBars; i++) {
			const amplitude = Math.min(waveformBuffer[i], maxAmplitude);
			const barHeight = amplitude * 2;
			const x = i * BAR_UNIT * scale;
			const barY = midLine - amplitude;
			ctx.fillRect(x, barY, BAR_WIDTH * scale, barHeight);
		}

		animFrameRef.current = requestAnimationFrame(drawWaveform);
	}, [canvasRef]);

	const start = useCallback(
		(stream: MediaStream) => {
			const canvas = canvasRef.current;
			if (!canvas) return;

			const audioCtx = new AudioContext();
			audioContextRef.current = audioCtx;
			const source = audioCtx.createMediaStreamSource(stream);
			const analyser = audioCtx.createAnalyser();

			analyser.fftSize = 2048;
			source.connect(analyser);
			analyserNodeRef.current = analyser;

			const totalBars = Math.floor(canvas.width / BAR_UNIT);
			waveformBufferRef.current = new Array(totalBars).fill(0);
			frameCountRef.current = 0;

			drawWaveform();
		},
		[canvasRef, drawWaveform],
	);

	const stop = useCallback(() => {
		if (animFrameRef.current) {
			cancelAnimationFrame(animFrameRef.current);
			animFrameRef.current = 0;
		}

		const canvas = canvasRef.current;
		if (canvas) {
			const ctx = canvas.getContext('2d');
			if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
		}

		if (analyserNodeRef.current) {
			analyserNodeRef.current.disconnect();
			analyserNodeRef.current = null;
		}

		if (audioContextRef.current) {
			audioContextRef.current.close();
			audioContextRef.current = null;
		}
	}, [canvasRef]);

	const resizeCanvas = useCallback(
		(width: number, height: number) => {
			const canvas = canvasRef.current;
			if (canvas) {
				canvas.width = width;
				canvas.height = height;
			}
		},
		[canvasRef],
	);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (animFrameRef.current) {
				cancelAnimationFrame(animFrameRef.current);
			}
			if (analyserNodeRef.current) {
				analyserNodeRef.current.disconnect();
			}
			if (audioContextRef.current) {
				audioContextRef.current.close();
			}
		};
	}, []);

	return { start, stop, resizeCanvas };
}
